import {
  PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from "react";
import { FilterChipType } from "../../../../components/search-filter/types";
import {
  RebatesFilterAction,
  RebatesFilterContextType,
  RebatesFilterStateType,
} from "../../types/types";

export const sectionIdConstants = {
  agreementNumber: "SELECTEDAGREEMENTNUMBER",
  seller: "SELECTEDSELLER",
  customer: "SELECTEDCUSTOMER",
  terms: "SELECTEDTERMS",
  salesRegions: "SELECTEDSALESREGIONS",
  comletionLevels: "SELECTEDCOMPLETIONLEVELS",
  validity: "SELECTEDVALIDITY",
  agreementName: "SELECTEDAGREEMENTNAME",
  year: "SELECTEDYEAR",
  goalStatus: "SELECTEDGOALSTATUS",
};

export const RebatesFilterContext =
  createContext<RebatesFilterContextType | null>(null);

const initialFilterState: RebatesFilterStateType = {
  selectedAgreementNumber: "",
  selectedSalesRegions: [],
  selectedCustomer: null,
  selectedSeller: undefined,
  selectedTerms: [],
  selectedYear: undefined,
  selectedValidity: undefined,
  selectedGoalStatusCodes: [],
};

const filterReducer = (
  state: RebatesFilterStateType,
  action: RebatesFilterAction
): RebatesFilterStateType => {
  switch (action.type) {
    case "SET_AGREEMENT_NUMBER":
      return { ...state, selectedAgreementNumber: action.payload };
    case "SET_CUSTOMER":
      return { ...state, selectedCustomer: action.payload };
    case "SET_SELLER":
      return { ...state, selectedSeller: action.payload };
    case "SET_YEAR":
      return { ...state, selectedYear: action.payload };
    case "SET_VALIDITY":
      return { ...state, selectedValidity: action.payload };
    case "SET_GOAL_STATUS":
      return { ...state, selectedGoalStatusCodes: action.payload };
    case "TOGGLE_SALES_REGION": {
      const exists = state.selectedSalesRegions.some(
        (r) => r.id === action.payload.id
      );
      return {
        ...state,
        selectedSalesRegions: exists
          ? state.selectedSalesRegions.filter((r) => r.id !== action.payload.id)
          : [...state.selectedSalesRegions, action.payload],
      };
    }
    case "TOGGLE_TERM": {
      const exists = state.selectedTerms.some(
        (t) => t.id === action.payload.id
      );
      return {
        ...state,
        selectedTerms: exists
          ? state.selectedTerms.filter((t) => t.id !== action.payload.id)
          : [...state.selectedTerms, action.payload],
      };
    }
    case "CLEAR":
      return initialFilterState;
  }
};

export const getChipsFromFilterState = (
  filterState: RebatesFilterStateType
) => {
  const {
    selectedAgreementNumber,
    selectedCustomer,
    selectedSalesRegions,
    selectedSeller,
    selectedTerms,
    selectedYear,
    selectedValidity,
    selectedGoalStatusCodes,
  } = filterState;
  const chips: FilterChipType[] = [];

  if (selectedAgreementNumber) {
    const sectionId = sectionIdConstants.agreementNumber;
    chips.push({ label: `No. ${selectedAgreementNumber}`, sectionId });
  }

  if (selectedCustomer) {
    const sectionId = sectionIdConstants.customer;
    chips.push({ label: selectedCustomer.label, sectionId });
  }

  if (selectedSeller) {
    const sectionId = sectionIdConstants.seller;
    chips.push({ label: selectedSeller.label, sectionId });
  }

  if (selectedSalesRegions.length > 0) {
    const sectionId = sectionIdConstants.salesRegions;
    chips.push(
      ...selectedSalesRegions.map((salesRegion) => ({
        label: salesRegion.name,
        sectionId,
      }))
    );
  }

  if (selectedTerms.length > 0) {
    const sectionId = sectionIdConstants.terms;
    chips.push(
      ...selectedTerms.map((term) => ({
        label: term.label,
        sectionId,
      }))
    );
  }

  if (selectedYear) {
    const sectionId = sectionIdConstants.year;
    chips.push({ label: `${selectedYear.value}`, sectionId });
  }

  if (selectedValidity) {
    const sectionId = sectionIdConstants.validity;
    if (selectedValidity.startDate && selectedValidity.endDate) {
      chips.push({
        label: [
          selectedValidity.startDate.toLocaleDateString(),
          selectedValidity.endDate.toLocaleDateString(),
        ].join(" - "),
        sectionId,
      });
    }
  }

  if (selectedGoalStatusCodes.length > 0) {
    const sectionId = sectionIdConstants.goalStatus;
    chips.push(
      ...selectedGoalStatusCodes.map((status) => ({
        label: status,
        sectionId,
      }))
    );
  }

  return chips;
};

const RebatesFilterProvider = ({ children }: PropsWithChildren<{}>) => {
  const [filterState, dispatch] = useReducer(filterReducer, initialFilterState);
  const [selectedFilterChips, setSelectedFilterChips] = useState<
    FilterChipType[]
  >([]);
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [openSectionIds, setOpenSectionIds] = useState<string[]>([]);

  const clearFilter = useCallback(() => {
    dispatch({ type: "CLEAR" });
    setSelectedFilterChips([]);
    setOpenSectionIds([]);
  }, []);

  const toggleSectionId = useCallback(
    (sectionId: string) =>
      setOpenSectionIds((previousState) =>
        previousState.includes(sectionId)
          ? previousState.filter((item) => item !== sectionId)
          : [...previousState, sectionId]
      ),
    []
  );

  const contextValue = useMemo(
    () => ({
      filterState,
      dispatch,
      filterIsOpen,
      setFilterIsOpen,
      clearFilter,
      selectedFilterChips,
      setSelectedFilterChips,
      openSectionIds,
      toggleSectionId,
      setOpenSectionIds,
    }),
    [
      filterState,
      filterIsOpen,
      clearFilter,
      selectedFilterChips,
      openSectionIds,
      toggleSectionId,
    ]
  );

  return (
    <RebatesFilterContext.Provider value={contextValue}>
      {children}
    </RebatesFilterContext.Provider>
  );
};

export { RebatesFilterProvider };
